// TanStack Query keys and hooks for the calendar routes. Consent changes arrive with the accounts
// list; a card's holds refetch on the stream's `calendar` event (applyFeedPlan) and, as a safety
// net, on the card's revision and a slow interval.
import { queryOptions, useMutation, useQuery, type QueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queries";
import { calendarApi } from "./calendarApi";

export const calendarKeys = {
  accounts: ["calendarAccounts"] as const,
  holds: (cardId: string, updatedAt: string) => ["calendarHolds", cardId, updatedAt] as const,
};

export const calendarAccountsQuery = queryOptions({
  queryKey: calendarKeys.accounts,
  queryFn: ({ signal }) => calendarApi.accounts(signal),
  staleTime: 30_000,
  refetchInterval: 120_000,
});

export function useCalendarAccounts() {
  return useQuery(calendarAccountsQuery);
}

/** The holds and bookings MeeMail made for a card, refreshed whenever the card itself changes. */
export function useCardHolds(cardId: string | null, updatedAt: string | null) {
  return useQuery({
    queryKey: calendarKeys.holds(cardId ?? "", updatedAt ?? ""),
    queryFn: ({ signal }) => calendarApi.holds(cardId ?? "", signal),
    enabled: !!cardId,
    staleTime: 15_000,
    // A hold settles a moment after the card event; this is only a safety net.
    refetchInterval: 60_000,
    retry: false,
  });
}

export function invalidateCalendar(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: calendarKeys.accounts });
  void queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
}

/** Ask the server for Google's calendar consent page and hand the browser over to it. */
export function useConnectCalendar() {
  return useMutation({
    mutationFn: async (accountId: string) => (await calendarApi.connect(accountId)).url,
    onSuccess: (url) => {
      if (/^https:\/\//i.test(url) || url.startsWith("/")) window.location.assign(url);
    },
  });
}
